/**
 * What a turn made, under its reply (Paper "Chat turn · one block", A1): its
 * docs, a new Area and (S-525) the routines it made or changed, one row each,
 * in place of the cards and lines it used to post.
 */
import { ChatIcon } from "@crew/ui/components/chat-icon";
import { cn } from "@crew/ui/lib/utils";
import { useContext } from "react";
import { Link } from "@crew/ui/components/link";
import { MadeList, MadeRow } from "@crew/ui/components/made-list";
import { Clock, FolderOpen } from "lucide-react";
import type { Me } from "../../app/me";
import { useDoc, type DocItem } from "../docs/api";
import { DocIcon } from "../docs/parts";
import { docRowView, routineRowView, type DocRowView } from "./made-view";
import { fileUrl } from "../messaging/attached";
import { ThreadRoutines } from "./thread-routines";
import type { MadeItem, Row } from "./turn-state";

/** A doc's row: live from the thread's `docs`, else read by id, as its card was. */
function DocRow({ item, listed }: { item: Extract<MadeItem, { kind: "doc" }>; listed?: DocItem }) {
  const fetched = useDoc(item.docId, !listed);
  const view = docRowView(item.title, listed ?? fetched.data, fetched.error, item.capture);
  return (
    <MadeRow
      data-doc-id={item.docId}
      icon={<DocIcon file={view.file} />}
      thumb={view.thumb ? <DocThumb thumb={view.thumb} /> : undefined}
      title={view.title}
      detail={view.detail}
      href={`/docs/${item.docId}`}
      muted={view.gone}
    />
  );
}

/** S-525: a kept page's tile, its image's thumbnail or a page of lines (Paper C4). */
function DocThumb({ thumb }: { thumb: NonNullable<DocRowView["thumb"]> }) {
  if (thumb.kind === "image")
    return <img alt="" loading="lazy" src={fileUrl(thumb.fileId, "thumb")} />;
  return (
    <span
      aria-hidden
      className="flex h-8 w-6 flex-col gap-[3px] rounded-[2px] bg-card px-1 pt-1.5 shadow-ring"
    >
      <span className="h-[2px] w-2.5 bg-foreground" />
      <span className="h-px w-full bg-hint" />
      <span className="h-px w-full bg-hint" />
      <span className="h-px w-3/4 bg-hint" />
    </span>
  );
}

function AreaRow({ item, me }: { item: Extract<MadeItem, { kind: "area" }>; me: Me }) {
  const area = me.areas.find((a) => a.slug === item.slug);
  return (
    <MadeRow
      icon={area ? <ChatIcon name={area.icon} size={16} /> : <FolderOpen strokeWidth={1.75} />}
      title={area?.label ?? item.label}
      detail="New area"
      href={`/areas/${item.slug}`}
      action={
        <Link
          href={`/areas/${item.slug}?move=1`}
          data-area-line={item.slug}
          className="text-foreground underline underline-offset-2"
        >
          Move chats and docs in
        </Link>
      }
    />
  );
}

/** A routine's row (S-525): live from the thread's `routines`, with when it runs next. */
function RoutineRow({ item, me }: { item: Extract<MadeItem, { kind: "routine" }>; me: Me }) {
  const routines = useContext(ThreadRoutines);
  const view = routineRowView(
    item,
    routines?.get(item.routineId),
    routines !== null,
    me.workspace.timezone,
    new Date(),
  );
  return (
    <MadeRow
      data-routine-id={item.routineId}
      icon={<Clock strokeWidth={1.75} />}
      title={view.title}
      detail={view.detail}
      note={
        view.note ? (
          <span className={cn(view.upcoming ? "text-moss" : "text-muted-foreground")}>
            {view.note}
          </span>
        ) : null
      }
      href={`/routines/${item.routineId}`}
      muted={view.gone}
    />
  );
}

export function TurnMade({
  row,
  docs,
  me,
}: {
  row: Extract<Row, { kind: "made" }>;
  docs: Map<string, DocItem>;
  me: Me;
}) {
  return (
    <div data-made={row.turnId} className="flex pl-10">
      <MadeList>
        {row.items.map((item) => {
          if (item.kind === "doc") {
            return <DocRow key={item.key} item={item} listed={docs.get(item.docId)} />;
          }
          if (item.kind === "routine") return <RoutineRow key={item.key} item={item} me={me} />;
          return <AreaRow key={item.key} item={item} me={me} />;
        })}
      </MadeList>
    </div>
  );
}
